import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import * as ella from "@/lib/ellaApi";
import { toast } from "sonner";

const emptyForm = {
  id: "",
  label: "",
  phone: "",
  state_id: "",
  city_id: "",
  district_id: "",
  postal_code: "",
  address: "",
  is_default: false,
};

export default function AddressFormModal({ open, onClose, onSaved, initial = null }) {
  const isEdit = !!initial;
  const [form, setForm] = useState(emptyForm);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [saving, setSaving] = useState(false);

  // Re-seed the form every time the modal opens (add fresh / edit an address)
  useEffect(() => {
    if (!open) return;
    setForm(initial ? {
      id: initial.ID || "",
      label: initial.Name || "",
      phone: initial.Phone || "",
      state_id: initial.StateID || "",
      city_id: initial.CityID || "",
      district_id: initial.DistrictID || "",
      postal_code: initial.PostalCode || "",
      address: initial.Address || "",
      is_default: Number(initial.IsDefault) === 1,
    } : emptyForm);
  }, [open, initial]);

  useEffect(() => {
    if (!open) return;
    ella.getStates().then((res) => res.status && setStates(res.data || [])).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (form.state_id) {
      ella.getCities(form.state_id).then((res) => res.status && setCities(res.data || [])).catch(() => {});
    } else { setCities([]); }
  }, [form.state_id]);

  useEffect(() => {
    if (form.city_id) {
      ella.getDistricts(form.city_id).then((res) => res.status && setDistricts(res.data || [])).catch(() => {});
    } else { setDistricts([]); }
  }, [form.city_id]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async (e) => {
    e?.preventDefault();
    if (!form.label || !form.phone || !form.address || !form.state_id || !form.city_id || !form.district_id) {
      toast.error("Lengkapi semua kolom"); return;
    }
    setSaving(true);
    try {
      const res = await ella.saveAddress({
        id: form.id || "",
        label: form.label,
        phone: form.phone,
        stateId: form.state_id,
        cityId: form.city_id,
        districtId: form.district_id,
        postalCode: form.postal_code,
        address: form.address,
        isDefault: form.is_default,
        action: isEdit ? "edit" : "add",
      });
      if (res.status) {
        toast.success(res.message || "Alamat tersimpan");
        onSaved?.();
        onClose();
      } else {
        toast.error(res.message || "Gagal menyimpan alamat");
      }
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md sm:rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="address-form-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{isEdit ? "Ubah Alamat" : "Tambah Alamat Baru"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSave} className="space-y-3">
          <div><Label>Label Alamat</Label>
            <Input data-testid="address-label-input" value={form.label} onChange={(e) => update("label", e.target.value)} placeholder="Rumah / Kantor" /></div>
          <div><Label>No. Telepon</Label>
            <Input data-testid="address-phone-input" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="08xxxxxxxxxx" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Provinsi</Label>
              <Select value={form.state_id} onValueChange={(v) => { update("state_id", v); update("city_id", ""); update("district_id", ""); }}>
                <SelectTrigger data-testid="address-state-select"><SelectValue placeholder="Pilih" /></SelectTrigger>
                <SelectContent className="max-h-60">{states.map((s) => <SelectItem key={s.ID} value={s.ID}>{s.Name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Kota/Kab.</Label>
              <Select value={form.city_id} onValueChange={(v) => { update("city_id", v); update("district_id", ""); }} disabled={!form.state_id}>
                <SelectTrigger data-testid="address-city-select"><SelectValue placeholder="Pilih" /></SelectTrigger>
                <SelectContent className="max-h-60">{cities.map((c) => <SelectItem key={c.ID} value={c.ID}>{c.Name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Kecamatan</Label>
              <Select value={form.district_id} onValueChange={(v) => update("district_id", v)} disabled={!form.city_id}>
                <SelectTrigger data-testid="address-district-select"><SelectValue placeholder="Pilih" /></SelectTrigger>
                <SelectContent className="max-h-60">{districts.map((d) => <SelectItem key={d.ID} value={d.ID}>{d.Name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Kode Pos</Label>
              <Input data-testid="address-postal-input" value={form.postal_code} onChange={(e) => update("postal_code", e.target.value)} placeholder="Opsional" /></div>
          </div>
          <div><Label>Alamat Lengkap</Label>
            <Textarea data-testid="address-detail-input" rows={3} value={form.address} onChange={(e) => update("address", e.target.value)} placeholder="Nama jalan, nomor rumah, RT/RW, patokan..." /></div>
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox checked={form.is_default} onCheckedChange={(v) => update("is_default", !!v)} data-testid="address-default-checkbox" />
            <span className="text-sm text-gray-700">Atur sebagai alamat utama</span>
          </label>
          <Button type="submit" disabled={saving} className="w-full h-11 btn-primary" data-testid="address-save-button">
            {saving ? "Menyimpan..." : "Simpan Alamat"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
